import { z } from 'zod';
import type { GymModuleKey } from '@bulk/types';
import { idSchema } from './common';
import { localizedText } from './website-blocks';

/** Every card the member home can hold, in the order a gym that never arranged it shows them. */
export const MEMBER_HOME_CARDS = ['announcements', 'membership', 'profile', 'balance', 'actions', 'visits', 'bookings', 'entry', 'notifications', 'documents', 'payments', 'quickLinks', 'trainers', 'hours', 'barMenu'] as const;
export type MemberHomeCard = (typeof MEMBER_HOME_CARDS)[number];

/** The member home as it was before it could be arranged; the cards added since stay off until someone switches them on. */
export const MEMBER_HOME_CARDS_ON = new Set<MemberHomeCard>(['announcements', 'membership', 'profile', 'balance', 'actions', 'visits', 'bookings', 'entry', 'notifications', 'documents', 'payments', 'quickLinks']);

/** A card for a module the gym has switched off never shows, whatever the order says. */
export const MEMBER_HOME_CARD_MODULES: Partial<Record<MemberHomeCard, GymModuleKey>> = {
  announcements: 'campaigns',
  balance: 'bar',
  bookings: 'classes',
  trainers: 'trainers',
  barMenu: 'menu',
};

export const MEMBER_HEADER_VARIANTS = ['left', 'centred'] as const;
export type MemberHeaderVariant = (typeof MEMBER_HEADER_VARIANTS)[number];

export const QUICK_LINK_ICONS = ['link', 'calendar', 'dumbbell', 'coffee', 'user', 'phone', 'map', 'star', 'gift', 'info'] as const;
export type QuickLinkIcon = (typeof QUICK_LINK_ICONS)[number];
export const QUICK_LINK_LIMIT = 6;

const HREF = /^(?:\/(?![/\\])[^\s\\]*|https?:\/\/[^\s]+)$/i;

export const quickLinkSchema = z.object({
  label: localizedText,
  href: z.string().trim().max(500).regex(HREF, 'A path such as /app/classes, or an http or https address'),
  icon: z.enum(QUICK_LINK_ICONS).default('link'),
});
export type QuickLink = z.infer<typeof quickLinkSchema>;

/** What the content team arranges, kept in the website document so it is drafted and published with the site. */
export const memberHomeSchema = z.object({
  cards: z
    .array(z.object({ key: z.enum(MEMBER_HOME_CARDS), enabled: z.boolean() }))
    .max(MEMBER_HOME_CARDS.length)
    .default([])
    .refine((cards) => new Set(cards.map((c) => c.key)).size === cards.length, 'A card is listed once'),
  welcome: z
    .record(z.string(), z.string().max(4000))
    .default({})
    .describe('Markdown in each language, shown as typed and sanitised when drawn'),
  heroAssetId: idSchema.nullable().default(null),
  quickLinks: z.array(quickLinkSchema).max(QUICK_LINK_LIMIT).default([]),
});
export type MemberHome = z.infer<typeof memberHomeSchema>;

export const PORTAL_LOOKS = ['bulk', 'site'] as const;
export type PortalLookKey = (typeof PORTAL_LOOKS)[number];

/** What Bulk decides for the gym in the design studio: which cards the content team may use, in what default order, the header, and whose look the sign-in pages and member area wear. */
export const memberAreaDesignSchema = z.object({
  cards: z
    .array(z.enum(MEMBER_HOME_CARDS))
    .default(() => [...MEMBER_HOME_CARDS])
    .refine((cards) => new Set(cards).size === cards.length, 'A card is listed once'),
  header: z.enum(MEMBER_HEADER_VARIANTS).default('left'),
  look: z.enum(PORTAL_LOOKS).default('bulk').describe("'bulk' keeps Bulk's own screens with the gym's fonts, corners and brand colours; 'site' dresses the sign-in pages and the member area in the gym's site design"),
});
export type MemberAreaDesign = z.infer<typeof memberAreaDesignSchema>;

/** The cards a member sees, in order: the content team's arrangement within what Bulk allows, then the allowed cards it never mentioned, minus switched-off modules. */
export function memberHomeCards(design: MemberAreaDesign | null, home: MemberHome | null, off: ReadonlySet<GymModuleKey>): MemberHomeCard[] {
  const allowed = design?.cards ?? [...MEMBER_HOME_CARDS];
  const arranged = (home?.cards ?? []).filter((card) => allowed.includes(card.key));
  const rest = allowed.filter((key) => !arranged.some((card) => card.key === key)).map((key) => ({ key, enabled: MEMBER_HOME_CARDS_ON.has(key) }));
  return [...arranged, ...rest].filter((card) => {
    const module = MEMBER_HOME_CARD_MODULES[card.key];
    return card.enabled && !(module && off.has(module));
  }).map((card) => card.key);
}
